import React, {
  ChangeEvent,
  KeyboardEvent,
  MutableRefObject,
  Reducer,
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { IInputProps, Omit } from '@rbi-ctg/frontend';
import { FloatingLabelInput } from 'components/floating-label-fields';
import useLocationPredictions from 'hooks/geolocation/use-location-predictions';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import useIdCounter from 'hooks/use-id-counter';
import useOnClickOutside from 'hooks/use-on-click-outside';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { FormValidationState } from 'utils/form';

import { messages } from './messages';
import theme from './theme';
import { ActionType, Actions, FocusRefs, IAddressInputState } from './types';

////////////////////////////////////////////////////////////
// Styles
////////////////////////////////////////////////////////////

const AutoCompleteAddressInputWrapper = styled.form`
  position: relative;
  width: 100%;
`;

const StyledFloatingLabelInput = styled<any>(FloatingLabelInput)`
  position: relative;
  width: 100%;

  & input {
    padding: 0 1rem;
  }

  & label {
    left: 1rem;
  }

  & input:focus {
    border: 1px solid ${theme.activeBorderColor};
    z-index: ${Styles.zIndex.top};
  }

  label {
    z-index: ${Styles.zIndex.overlay};
  }
`;

const PredictionList = styled.ul`
  font-size: 0.7em;
  left: 0;
  list-style-type: none;
  margin: 0;
  overflow: hidden;
  padding: 0;
  position: absolute;
  right: 0;
  top: 49px;
  z-index: ${Styles.zIndex.normal};
`;

const PredictionItem = styled.li`
  & + & {
    margin-top: -1px;
  }
`;

const PredictionBtn = styled.button`
  appearance: none;
  background-color: ${theme.backgroundColor};
  border: 1px solid ${theme.inactiveBorderColor};
  border-bottom: 1px solid ${theme.inactiveSeparatorColor};
  color: inherit;
  display: block;
  font-size: inherit;
  margin: 0; // fixes visual bug on list dropdown
  padding: 1rem;
  position: relative;
  text-align: left;
  width: 100%;

  &:focus {
    border-color: ${theme.activeBorderColor};
    outline: none;
    z-index: ${Styles.zIndex.below};
  }

  ${PredictionItem}:last-of-type & {
    border-radius: 0 0 ${Styles.borderRadius} ${Styles.borderRadius};
  }
`;

const MainPredictionText = styled.span`
  font-weight: bold;
  margin-right: 0.5em;
`;

const SecondaryPredictionText = styled.span`
  font-weight: 200;
`;

interface IAutocompleteAddressInputProps extends Omit<IInputProps, 'onChange'> {
  autoFocus?: boolean;
  initialValue?: string;
  innerRef?: MutableRefObject<HTMLInputElement | null>;
  forceHideOnClickOutside?: boolean;
  label: string;
  message?: string;
  matchTypes?: ('address' | '(region)' | 'geocode' | 'establishment' | '(cities)')[];
  onAddressChange: (address: string, placeId?: string) => void;
  ariaKey?: string;
  required: boolean;
  validation?: FormValidationState | null;
  'data-testid': string;
}

type Predictions = (google.maps.places.AutocompletePrediction & { id: string })[];

////////////////////////////////////////////////////////////
// Component
////////////////////////////////////////////////////////////

const reducer: Reducer<IAddressInputState, Actions> = (state, action) => {
  switch (action.type) {
    case ActionType.ADDRESS_CHANGED:
      return {
        ...state,
        forceHidePredictions: false,
        skipPredictions: false,
        address: action.payload,
      };

    case ActionType.ADDRESS_CHANGED_PROP:
      return {
        ...state,
        skipPredictions: false,
        address: action.payload,
      };

    case ActionType.PRESSED_DOWN:
      return {
        ...state,
        forceHidePredictions: false,
        focusedIndex: Math.min(state.focusedIndex + 1, action.payload.length - 1),
      };

    case ActionType.PRESSED_UP:
      return {
        ...state,
        focusedIndex: Math.max(state.focusedIndex - 1, 0),
      };

    case ActionType.USER_FOCUSED:
      return {
        ...state,
        focusedIndex: 0,
        forceHidePredictions: false,
      };

    case ActionType.ON_CLICK_OUTSIDE:
      return {
        ...state,
        focusedIndex: -1,
        forceHidePredictions: action.payload,
      };

    case ActionType.USER_BLURRED:
    case ActionType.PRESSED_ESCAPE:
      (document.activeElement as HTMLInputElement).blur();
      return {
        ...state,
        focusedIndex: -1,
        forceHidePredictions: true,
      };

    case ActionType.SELECTED_PREDICTION:
      return {
        ...state,
        focusedIndex: -1,
        address: action.payload,
        skipPredictions: true,
        forceHidePredictions: true,
      };

    default:
      return state;
  }
};

function AutocompleteAddressInput({
  autoFocus = false,
  className = '',
  initialValue = '',
  innerRef,
  matchTypes = [],
  forceHideOnClickOutside = false,
  label,
  message,
  onAddressChange,
  required,
  validation,
  value,
  ariaKey,
  'data-testid': testId,
}: IAutocompleteAddressInputProps) {
  //--------------------------------------------------------
  // Setup
  //--------------------------------------------------------
  // Allows the user to define a prefix e.g. `storelocator`
  let uid = useIdCounter('autocomplete-address');
  const ariaIdPrefix = ariaKey ? ariaKey : uid;

  const internalCreatedRef = useRef<HTMLInputElement | null>(null);
  const [state, dispatch] = useReducer(reducer, {
    forceHidePredictions: false,
    skipPredictions: false,
    focusedIndex: -1,
    address: initialValue,
  });
  const labelId = `${ariaIdPrefix}-label`;
  const inputId = `${ariaIdPrefix}-input`;
  const divId = `${ariaIdPrefix}-combobox`;
  const ulId = `${ariaIdPrefix}-listbox`;
  // only set the aria-activedescendant if one of the li have focus
  const ariaActivedescendant =
    state.focusedIndex >= 1 ? `result-item-${state.focusedIndex - 1}` : '';

  const predictions = (useLocationPredictions(state.address, {
    skipPredictions: state.skipPredictions,
    includeExactMatch: true,
    matchTypes,
  }) as unknown) as Predictions;
  const internalRef = innerRef || internalCreatedRef;

  //--------------------------------------------------------
  // Prop management
  //--------------------------------------------------------

  useEffect(() => {
    if (typeof value === 'string') {
      dispatch({ type: ActionType.ADDRESS_CHANGED_PROP, payload: value });
    }
  }, [value]);

  //--------------------------------------------------------
  // Focus management
  //--------------------------------------------------------

  const predictionRefs = useMemo(
    () => predictions.map(() => createRef<HTMLButtonElement>()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [predictions.length]
  );

  const focusRefs = useMemo<FocusRefs>(() => [internalRef, ...predictionRefs], [
    internalRef,
    predictionRefs,
  ]);

  useEffectOnUpdates(() => {
    if (focusRefs[state.focusedIndex] && focusRefs[state.focusedIndex]!.current) {
      focusRefs[state.focusedIndex].current!.focus();
    }
  }, [focusRefs, state.focusedIndex, autoFocus]);

  const handleKeyUp = useCallback(
    (event: KeyboardEvent<HTMLFormElement>) => {
      switch (event.key) {
        case 'ArrowDown':
          return dispatch({ type: ActionType.PRESSED_DOWN, payload: focusRefs });

        case 'ArrowUp':
          return dispatch({ type: ActionType.PRESSED_UP });
        default:
          return;
      }
    },
    [dispatch, focusRefs]
  );

  const hasFocus = focusRefs.filter(Boolean).some(ref => document.activeElement === ref.current);

  const OutsideClickWrapper = useOnClickOutside(() =>
    dispatch({ type: ActionType.ON_CLICK_OUTSIDE, payload: forceHideOnClickOutside } as Actions)
  );

  const handleKeyDown = useCallback(
    event => {
      if (event.key === 'Escape' && hasFocus) {
        event.stopPropagation();
        dispatch({ type: ActionType.PRESSED_ESCAPE });
      }
    },
    [hasFocus]
  );

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      if (predictions.length > 0) {
        event.preventDefault();
        const prediction = predictions[0];
        const address = prediction.description;
        dispatch({ type: ActionType.SELECTED_PREDICTION, payload: address });
        onAddressChange(address, prediction.place_id);
      }
    },
    [onAddressChange, predictions]
  );

  const handleBlur = useCallback(() => {
    if (predictions.length === 1) {
      const address = predictions[0].description;
      dispatch({ type: ActionType.SELECTED_PREDICTION, payload: address });
      onAddressChange(address, predictions[0].place_id);
    }
  }, [predictions, onAddressChange]);

  // Footer controls the display of the list with address suggestions
  // We should render it only if the address input is in focus and not empty,
  // and is not explicitly hidden with `forceHidePredictions`
  const shouldRenderFooter = !!state.address && !state.forceHidePredictions && hasFocus;

  const { formatMessage } = useIntl();
  const mParticle = useMParticleContext();

  return (
    <OutsideClickWrapper>
      <AutoCompleteAddressInputWrapper
        id={divId}
        aria-owns={ulId}
        aria-expanded={predictions.length > 0}
        aria-haspopup="listbox"
        role="combobox"
        autoComplete="off"
        onBlur={handleBlur}
        onSubmit={handleSubmit}
        onKeyUp={handleKeyUp}
        onKeyDown={handleKeyDown}
      >
        <StyledFloatingLabelInput
          data-testid={testId}
          autoFocus={autoFocus === undefined ? true : autoFocus}
          className={className}
          label={label}
          labelId={labelId}
          validation={validation}
          message={message}
          value={state.address}
          ref={internalRef}
          onFocus={() => dispatch({ type: ActionType.USER_FOCUSED })}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            const address = event.target.value;
            dispatch({ type: ActionType.ADDRESS_CHANGED, payload: address });
            onAddressChange(address);
          }}
          autoComplete="off"
          required={required}
          id={inputId}
          aria-autocomplete="list"
          aria-controls={ulId}
          aria-activedescendant={ariaActivedescendant}
          hasFooter={shouldRenderFooter}
          footer={
            <PredictionList
              id={ulId}
              role="listbox"
              aria-labelledby={labelId}
              data-testid="address-prediction-list"
              hidden={!shouldRenderFooter}
            >
              {predictions.length === 0 && !!state.address ? (
                <PredictionItem key="empty">
                  <PredictionBtn type="button">
                    <SecondaryPredictionText>
                      {formatMessage(messages.noLocationsMatchYourSearch)}
                    </SecondaryPredictionText>
                  </PredictionBtn>
                </PredictionItem>
              ) : (
                predictions.map((prediction, index: number) => (
                  <PredictionItem
                    data-private
                    key={prediction.id}
                    id={`result-item-${index}`}
                    role="option"
                  >
                    <PredictionBtn
                      data-testid={`prediction-${index}`}
                      type="button"
                      ref={predictionRefs[index]}
                      onClick={() => {
                        const address = prediction.description;
                        dispatch({ type: ActionType.SELECTED_PREDICTION, payload: address });
                        onAddressChange(address, prediction.place_id);
                        // this works for both clicking and pressing enter because of accessability
                        mParticle.logEvent(
                          CustomEventNames.MANUAL_LOCATION_SEARCH,
                          EventTypes.Search
                        );
                      }}
                    >
                      <>
                        <MainPredictionText>
                          {prediction.structured_formatting.main_text}
                        </MainPredictionText>
                        <SecondaryPredictionText>
                          {prediction.structured_formatting.secondary_text}
                        </SecondaryPredictionText>
                      </>
                    </PredictionBtn>
                  </PredictionItem>
                ))
              )}
            </PredictionList>
          }
        />
      </AutoCompleteAddressInputWrapper>
    </OutsideClickWrapper>
  );
}

AutocompleteAddressInput.defaultProps = {
  onAddressChange: () => {},
};

export default React.forwardRef(
  (props: IAutocompleteAddressInputProps, ref: MutableRefObject<HTMLInputElement | null>) => (
    <AutocompleteAddressInput {...props} innerRef={ref} />
  )
);
